import React, { useState, useRef } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import Search from "../img/Search.svg";
import Sort from "../img/Sort.svg";
import api from "../../api/axiosInstance";
import DopePDF from "../img/MarketPlace.pdf";

const HomeHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);

  const handleClickLogin = () => {
    navigate("/Login");
    setIsMobileMenuOpen(false);
  };

  const handleClickSignUp = () => {
    navigate("/mainLogin");
    setIsMobileMenuOpen(false);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.trim() === "") {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchSearchResults(value);
    }, 300);
  };

  const fetchSearchResults = async (context) => {
    try {
      const response = await api.get(
        "/display/api/v1/list-project-for-display",
        {
          params: { context },
        }
      );
      if (response.data?.status === "success") {
        setSearchResults(response.data.data.results || []);
        setShowDropdown(true);
      }
    } catch (error) {
      console.error("Search failed:", error);
      setSearchResults([]);
      setShowDropdown(false);
    }
  };

  // Navigate to internal guidelines viewer
  const handleClickGuidelines = () => {
    setIsMobileMenuOpen(false);
    navigate("/guidelines");
  };

  return (
    <header className="w-full h-16  sticky top-0 z-50 flex items-center justify-between px-5 sm:px-10 bg-[#4C3A68] relative z-20">
      {/* Left: Logo & Search */}
      <div className="flex items-center w-full md:w-auto justify-between md:gap-4">
        <NavLink
          to="/"
          className="font-bold text-xl md:text-2xl text-white whitespace-nowrap cursor-pointer"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          Onzitr Marketplace
        </NavLink>

        <div className="relative hidden md:block w-40 md:w-72">
          <img
            src={Search}
            alt="Search"
            className="absolute left-2 w-4 h-4 top-2"
          />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            className="w-full rounded-md font-semibold focus:outline-none pl-8 pr-2 py-1.5 text-sm bg-[#423756] border border-[#4E4958] shadow-sm shadow-black/25 text-white placeholder-gray-300"
          />
          {showDropdown && (
            <div className="absolute top-10 left-0 right-0 bg-white text-black z-10 border border-gray-300 rounded shadow max-h-60 overflow-y-auto">
              {searchResults.map((project) => (
                <div
                  key={project.id}
                  onMouseDown={() => {
                    setSearchTerm(project.name);
                    setShowDropdown(false);
                    navigate("/AppDetails", { state: { id: project.id } });
                  }}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                >
                  {project.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile hamburger */}
      <div className="sm:hidden flex items-center">
        <button
          onClick={toggleMobileMenu}
          className="text-white focus:outline-none"
        >
          <img src={Sort} alt="Menu" className="w-6 h-6" />
        </button>
      </div>

      {/* Desktop menu & auth */}
      <nav className="hidden sm:flex items-center gap-4 h-8">
        <div className="flex gap-4 text-white text-sm">
          <span
            onClick={() => {
              navigate("/");
              setIsMobileMenuOpen(false);
            }}
            role="link"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                navigate("/");
                setIsMobileMenuOpen(false);
              }
            }}
            className={`cursor-pointer border-b-2 transition duration-200 ${
              location.pathname === "/" || location.pathname === ""
                ? "border-[#9061DB] font-semibold"
                : "border-transparent hover:border-[#9061DB]"
            }`}
          >
            Home
          </span>

          <span
            onClick={() => {
              navigate("/products");
              setIsMobileMenuOpen(false);
            }}
            role="link"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                navigate("/products");
                setIsMobileMenuOpen(false);
              }
            }}
            className={`cursor-pointer border-b-2 transition duration-200 ${
              location.pathname.startsWith("/products")
                ? "border-[#9061DB] font-semibold"
                : "border-transparent hover:border-[#9061DB]"
            }`}
          >
            Products
          </span>

          <span
            onClick={handleClickGuidelines}
            role="link"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") handleClickGuidelines();
            }}
            className={`cursor-pointer border-b-2 transition duration-200 ${
              location.pathname.startsWith("/guidelines")
                ? "border-[#9061DB] font-semibold"
                : "border-transparent hover:border-[#9061DB]"
            }`}
            style={{ fontSize: "0.875rem" }}
          >
            Guidelines
          </span>
        </div>

        <div className="flex gap-2 md:gap-4 h-8">
          <button
            className="text-xs font-semibold px-5 rounded-full text-white flex items-center justify-center bg-[#8C62E2] shadow-md backdrop-blur-sm"
            onClick={handleClickSignUp}
          >
            Sign Up/Login
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="sm:hidden absolute top-16 left-0 w-full bg-[#4C3A68] z-10 text-white p-5 shadow-md flex flex-col gap-4">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `cursor-pointer pb-1 border-b-2 transition duration-200 ${
                isActive
                  ? "border-white font-semibold"
                  : "border-transparent hover:border-gray-300"
              }`
            }
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Home
          </NavLink>

          <NavLink
            to="/Products"
            className={({ isActive }) =>
              `cursor-pointer pb-1 border-b-2 transition duration-200 ${
                isActive
                  ? "border-white font-semibold"
                  : "border-transparent hover:border-gray-300"
              }`
            }
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Products
          </NavLink>

          {/* <span
            onClick={handleClickGuidelines}
            role="link"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") handleClickGuidelines();
            }}
            className="cursor-pointer font-normal pb-1 border-b-2 border-transparent hover:border-gray-300 transition duration-200"
            style={{ fontSize: "0.875rem" }}
          >
            Guidelines
          </span> */}

          {/* Guidelines as clickable span opening PDF, aligned flush left */}
          <span
            onClick={() =>
              window.open(DopePDF, "_blank", "noopener,noreferrer")
            }
            role="link"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                window.open(DopePDF, "_blank", "noopener,noreferrer");
              }
            }}
            className="pb-1 border-transparent font-normal text-white cursor-pointer select-none hover:border-gray-300"
            style={{ fontSize: "0.875rem" }}
          >
            Guidelines
          </span>

          {/* <button
            className="text-xs font-semibold rounded-xl text-white flex items-center justify-center bg-[#5E4D77] shadow-md backdrop-blur-sm h-8"
            onClick={handleClickLogin}
          >
            Sign in
          </button>
          <button
            className="text-xs font-semibold rounded-xl text-white flex items-center justify-center bg-[#8C62E2] shadow-md backdrop-blur-sm h-8"
            onClick={handleClickSignUp}
          >
            Sign up
          </button> */}

          <div className="flex justify-center items-center">
            <button
              className=" text-xs font-semibold w-36 h-8 rounded-full py-1.5 text-white flex items-center justify-center bg-[#8C62E2] shadow-md backdrop-blur-sm"
              onClick={handleClickSignUp}
            >
              Sign Up/Login
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default HomeHeader;
